<script lang="ts" setup>
// Vue core
import {computed, defineAsyncComponent} from 'vue';
import {useI18n} from 'vue-i18n';

// Types
import type {ReccoArticleDTO} from '@/ts/types/dto/recco/article.dto';
import type {ReccoAudioDTO} from '@/ts/types/dto/recco/audio.dto';
import type {ReccoVideoDTO} from '@/ts/types/dto/recco/video.dto';
import {ReccoRecommendationTypeDTO} from '@/ts/types/dto/recco/recommendation.dto';
import {ReccoMediaType} from '@/ts/types/component/recco/media.type';
import {buildImageTransformUrl} from '@/ts/utils/pure-functions';
import {mediaLabelWithDuration} from '@/ts/utils/recco-label-keys';

// Components
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';

// Lazy-loaded components
const LazyImage = defineAsyncComponent(async() => import('@/vue/atoms/lazy-image.vue'));
const BalmMediaPlayer = defineAsyncComponent(async() => import('@/vue/molecules/balm-media-player.vue'));

const props = defineProps<{
  recommendation: ReccoArticleDTO | ReccoAudioDTO | ReccoVideoDTO;
}>();

const isArticle = computed(() => props.recommendation.type === ReccoRecommendationTypeDTO.Article);
const isAudio = computed(() => props.recommendation.type === ReccoRecommendationTypeDTO.Audio);

const labelLanguageKey = computed(() => ({
  [ReccoRecommendationTypeDTO.Article]: 'recco_media_type_articles',
  [ReccoRecommendationTypeDTO.Audio]: 'recco_media_type_audios',
  [ReccoRecommendationTypeDTO.Video]: 'recco_media_type_videos',
})[props.recommendation.type]);

const {t: $t} = useI18n();
const title = props.recommendation.headline;

// "Listen · 12 min" (IPD-3375). Only the media carry a length to print: an article's `duration`
// is a reading estimate the design does not show here.
const mediaLabel = computed(() => isArticle.value
  ? $t(labelLanguageKey.value)
  : mediaLabelWithDuration($t(labelLanguageKey.value), (props.recommendation as ReccoAudioDTO | ReccoVideoDTO).duration, $t));

const POSTER_FORMAT = 'webp';

// Poster budget is ~2x the rendered card size already, so no extra DPR factor is applied.
// The audio poster dimensions are owned by balm-media-player (it re-requests its own
// size), so this budget only drives the article (lazy-image) and video (raw <img>) posters.
const posterUrl = computed(() => {
  const baseUrl = props.recommendation.dynamicImageResizingUrl;
  if (!baseUrl) {
    return '';
  }
  return buildImageTransformUrl(baseUrl, {
    width: 1116,
    height: 628,
    format: POSTER_FORMAT,
  });
});
</script>

<template>
  <div class="recommendation-content-card">
    <q-card flat class="content-card">
      <div class="content-card-media-section">
        <lazy-image
          v-if="isArticle"
          :src="posterUrl"
          loading="eager"
          :alt="props.recommendation.imageAlt ?? ''"
          class="image"
        />

        <balm-media-player
          v-else-if="isAudio"
          :src="(props.recommendation as ReccoAudioDTO).audioUrl ?? ''"
          :media-type="ReccoMediaType.Audio"
          :poster-src="posterUrl"
          :poster-alt="props.recommendation.imageAlt ?? ''"
        />

        <balm-media-player
          v-else
          :src="(props.recommendation as ReccoVideoDTO).videoUrl ?? ''"
          :media-type="ReccoMediaType.Video"
          :poster-src="posterUrl"
          :poster-alt="props.recommendation.imageAlt ?? ''"
          :warning-before-playing="(props.recommendation as ReccoVideoDTO).warning ?? null"
        />
      </div>

      <q-card-section class="content-card-bottom-section">
        <span class="media-card-type">{{ mediaLabel }}</span>
        <div class="title-row">
          <span class="title">{{ title }}</span>
          <svg-illustration name="right-down-arrow.svg" inner-class="inner-right"/>
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>

<style lang="scss" scoped>
.recommendation-content-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-wrap: wrap;
  width: 100%;
}

.content-card {
  width: 100%;
  border-radius: var(--card-border-radius);
}

.content-card-media-section {
  width: 100%;
  aspect-ratio: 16 / 9;

  .image {
    height: 100%;
    border-top-left-radius: var(--card-border-radius);
    border-top-right-radius: var(--card-border-radius);
  }
}

.content-card-bottom-section {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-1, 8px);
  background: var(--Neutral-UI-neutral-ui-20);
  padding: var(--Spacing-spacing-6, 42px);
  align-items: flex-start;

  .media-card-type {
    @include font-caption-sm;
    color: var(--Neutral-UI-neutral-ui-70);
  }

  .title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: var(--Spacing-spacing-2);
  }

  .title {
    @include font-h1;
    color: var(--Neutral-UI-neutral-ui-100);
  }
}
</style>